import { Send, Check, CheckCheck } from "lucide-react";

/**
 * TelegramBotMock — stylised, static rendition of a conversation
 * with the Lumfi Telegram bot. Used in /for/:slug landing pages as
 * VISUAL PROOF that the bot exists (replaces the previous screenshot
 * of the static `/bots-guide` page which didn't show the actual chat).
 *
 * Strategy
 * --------
 * - Pure React + Tailwind: zero runtime data, zero network. Works in
 *   SSR and any environment.
 * - Bubbles approximate Telegram's look (user pill on the right, bot
 *   tile on the left) without claiming to BE Telegram — we use the
 *   "Lumfi Bot" brand mark so it's clearly our product.
 * - The script is curated for the developer archetype: registering a
 *   client payment from Stripe and asking for the monthly safe spend.
 *
 * @param {Array<{from:"user"|"bot", text:string, time:string}>} messages
 *   Optional. Falls back to a developer-flavoured demo conversation.
 * @param {string} [title]
 */
export function TelegramBotMock({ messages, title = "Lumfi Bot" }) {
  const script = messages || [
    {
      from: "user",
      text: "Cobré 4.500 USD de Stripe (Vercel SaaS Consulting)",
      time: "08:42",
    },
    {
      from: "bot",
      text: "Registrado en Cuenta principal · USD\n\nIngreso: +4.500 USD\nFx aplicado: 4.149 COP/USD\nNuevo saldo: 18.7M COP\n\n¿Quieres adjuntar el invoice?",
      time: "08:42",
    },
    {
      from: "user",
      text: "/saldo",
      time: "09:15",
    },
    {
      from: "bot",
      text: "Seguro de usar esta semana: $ 1.250.000 COP\nRiesgo del mes (gastos próximos): $ 3.180.000 COP\nMRR neto activo: $ 12.6M COP\n\nIngresos esperados (7d):\n• Stripe + $ 2.4M COP · jue\n• GitLab + $ 1.8M COP · vie",
      time: "09:15",
    },
  ];

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-[#17212B] overflow-hidden shadow-lg"
      data-testid="mock-telegram-bot"
    >
      {/* Telegram-style header */}
      <div className="px-4 py-3 bg-[#1F2C39] border-b border-black/30 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-bold text-sm shadow-sm">
          L
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm leading-tight">{title}</div>
          <div className="text-[10px] text-emerald-400 leading-tight mt-0.5">
            en línea · responde en menos de 2s
          </div>
        </div>
        <span className="text-[10px] text-slate-400 uppercase tracking-wider">Telegram</span>
      </div>

      {/* Chat surface */}
      <div className="p-4 space-y-2.5 bg-[#0E1621] min-h-[260px]">
        {script.map((m, i) => {
          const isUser = m.from === "user";
          return (
            <div
              key={i}
              className={`flex ${isUser ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[78%] rounded-2xl px-3 py-2 text-[12px] leading-relaxed shadow-sm whitespace-pre-line ${
                  isUser
                    ? "bg-[#3A6FB0] text-white rounded-br-md"
                    : "bg-[#182533] text-slate-100 rounded-bl-md border border-white/5"
                }`}
                data-testid={`tg-bubble-${i}`}
              >
                <div>{m.text}</div>
                <div className={`flex items-center gap-1 mt-1 ${isUser ? "justify-end" : "justify-start"}`}>
                  <span className={`text-[9px] ${isUser ? "text-sky-100/70" : "text-slate-400"}`}>
                    {m.time}
                  </span>
                  {isUser && <CheckCheck className="h-2.5 w-2.5 text-sky-200" />}
                  {!isUser && i === script.length - 1 && (
                    <Check className="h-2.5 w-2.5 text-emerald-400" />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Fake input bar (purely decorative) */}
      <div className="px-3 py-2 bg-[#1F2C39] border-t border-black/30 flex items-center gap-2">
        <div className="flex-1 h-8 rounded-full bg-[#0E1621] border border-white/5 px-3 flex items-center text-[11px] text-slate-500">
          Escribe un mensaje o /comando…
        </div>
        <button
          className="h-8 w-8 rounded-full bg-emerald-600 hover:bg-emerald-500 transition-colors flex items-center justify-center text-white shadow-sm"
          type="button"
          aria-label="Enviar"
          data-testid="tg-send"
        >
          <Send className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
